import { OrgInfo } from '~/modules/services/userService';
import { PERSISTED_ORG_FIELDS } from '~/modules/stores/orgContextStore';

export const SALESFORCE_ORG_ID_PREFIX_LENGTH = 15;

interface OrgValidationState {
    hasLoaded: boolean;
    totalCount: number;
    orgs: OrgInfo[];
}

export function areSameOrgIds(firstOrgId: string | null | undefined, secondOrgId: string | null | undefined): boolean {
    if (!firstOrgId || !secondOrgId) {
        return false;
    }
    return (
        firstOrgId.slice(0, SALESFORCE_ORG_ID_PREFIX_LENGTH) === secondOrgId.slice(0, SALESFORCE_ORG_ID_PREFIX_LENGTH)
    );
}

// Shared by header.ts and selectOrg.ts: once the org list resolves, a persisted selection that no
// longer appears in it (revoked access, org removed) is stale and should be cleared. Pure
// predicate only — callers own the clearSelectedOrg()/sendNotification() side effects themselves,
// matching this codebase's convention of notifying from the component layer, not the store.
export function isStaleSelectedOrg(selectedOrg: OrgInfo | null, orgs: OrgInfo[]): boolean {
    return Boolean(selectedOrg) && !orgs.some((org) => areSameOrgIds(org.orgId, selectedOrg?.orgId));
}

export function getValidatedSelectedOrg(
    selectedOrg: OrgInfo | null,
    { hasLoaded, totalCount, orgs }: OrgValidationState,
): OrgInfo | null {
    if (!hasLoaded || orgs.length !== totalCount || isStaleSelectedOrg(selectedOrg, orgs)) {
        return null;
    }
    return selectedOrg;
}

// Limited to PERSISTED_ORG_FIELDS: a selectedOrg rehydrated from the cookie never carries
// tenantKey/instanceId (they're deliberately excluded from persistence), so comparing those two
// against a freshly-fetched org would report "changed" on every load, not just on a real drift.
function isSameOrgInfo(a: OrgInfo, b: OrgInfo): boolean {
    return PERSISTED_ORG_FIELDS.every((field) => a[field] === b[field]);
}

// Shared by header.ts and selectOrg.ts: the persisted selection (cookie, up to 7 days old) can
// still match an orgId in the fresh list while other fields — most visibly the edition badge —
// have changed server-side since it was cached. Returns the fresh copy only when it differs, so
// callers know to refresh the store/cookie; returns null for "still stale" or "already current".
export function getUpdatedSelectedOrg(selectedOrg: OrgInfo | null, orgs: OrgInfo[]): OrgInfo | null {
    if (!selectedOrg) {
        return null;
    }
    const freshOrg = orgs.find((org) => areSameOrgIds(org.orgId, selectedOrg.orgId));
    return freshOrg && !isSameOrgInfo(freshOrg, selectedOrg) ? freshOrg : null;
}
